import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Search, FileText, ScanLine, Calendar, Check, RotateCcw, Loader2 } from "lucide-react";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function monthKey(d) {
    if (!d) return "";
    return d.slice(0, 7); // "YYYY-MM"
}
function monthLabel(key) {
    if (!key) return "All";
    const [y, m] = key.split("-");
    return `${MONTH_LABELS[Number(m) - 1] || m} ${y}`;
}

export default function Invoices() {
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("all");
    const [busyId, setBusyId] = useState("");

    const reload = () => api.get("/invoices", { params: { q, limit: 500 } }).then((r) => setList(r.data));
    useEffect(() => { reload(); }, [q]);

    const togglePaid = async (inv, paid) => {
        setBusyId(inv.id);
        try {
            await api.post(`/invoices/${inv.id}/${paid ? "mark-paid" : "mark-unpaid"}`);
            toast.success(paid ? "Invoice marked paid" : "Invoice marked unpaid");
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update payment status");
        } finally { setBusyId(""); }
    };

    // Build the month toggle from whatever invoices are loaded, most-recent first.
    const monthBuckets = useMemo(() => {
        const set = new Map(); // key -> count
        list.forEach((i) => {
            const k = monthKey(i.invoice_date);
            if (!k) return;
            set.set(k, (set.get(k) || 0) + 1);
        });
        return Array.from(set.entries())
            .sort(([a], [b]) => (a < b ? 1 : -1))
            .map(([k, count]) => ({ key: k, label: monthLabel(k), count }));
    }, [list]);

    const filtered = useMemo(() => {
        if (selectedMonth === "all") return list;
        return list.filter((i) => monthKey(i.invoice_date) === selectedMonth);
    }, [list, selectedMonth]);

    // Monthly summary for the current filter
    const monthTotals = useMemo(() => {
        return filtered.reduce((a, i) => {
            const isCancelled = i.status === "cancelled";
            if (isCancelled) return a;
            a.count += 1;
            a.taxable += i.subtotal || 0;
            a.tax += i.total_tax || 0;
            a.grand += i.grand_total || 0;
            return a;
        }, { count: 0, taxable: 0, tax: 0, grand: 0 });
    }, [filtered]);

    return (
        <div data-testid="invoices-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Invoices</h1>
                    <p className="text-slate-500 mt-1 text-sm">All invoices are permanently stored and immutable once saved</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Link to="/invoices/from-po" className="hdb-btn hdb-btn-mint" data-testid="import-po-btn">
                        <ScanLine size={14} /> Import from PO PDF
                    </Link>
                    <Link to="/invoices/new" className="hdb-btn hdb-btn-primary" data-testid="new-invoice-btn">
                        + New Invoice
                    </Link>
                </div>
            </div>

            {/* Month toggle */}
            {monthBuckets.length > 0 && (
                <div className="hdb-card mb-4 p-3" data-testid="month-toggle">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold flex items-center gap-1 pr-2">
                            <Calendar size={12} /> Month
                        </div>
                        <button
                            onClick={() => setSelectedMonth("all")}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                                selectedMonth === "all"
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-900"
                            }`}
                            data-testid="month-all">
                            All <span className="opacity-60 ml-1">{list.length}</span>
                        </button>
                        {monthBuckets.map((b) => (
                            <button key={b.key}
                                    onClick={() => setSelectedMonth(b.key)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                                        selectedMonth === b.key
                                            ? "bg-slate-900 text-white border-slate-900"
                                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-900"
                                    }`}
                                    data-testid={`month-${b.key}`}>
                                {b.label} <span className="opacity-60 ml-1">{b.count}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Monthly summary strip */}
            {selectedMonth !== "all" && monthTotals.count > 0 && (
                <div className="hdb-card mb-4 p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label={`Invoices · ${monthLabel(selectedMonth)}`} value={monthTotals.count} />
                    <Stat label="Taxable" value={fmt(monthTotals.taxable)} />
                    <Stat label="Total tax" value={fmt(monthTotals.tax)} />
                    <Stat label="Grand total" value={fmt(monthTotals.grand)} highlight />
                </div>
            )}

            <div className="hdb-card mb-5 p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                    <input className="hdb-input pl-9" placeholder="Search invoice # or client…"
                           value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" />
                </div>
            </div>

            <div className="hdb-card overflow-hidden">
                <table className="hdb-table w-full">
                    <thead>
                        <tr>
                            <th>Invoice #</th>
                            <th>Date</th>
                            <th>Buyer</th>
                            <th>Type</th>
                            <th className="text-right">Taxable</th>
                            <th className="text-right">Tax</th>
                            <th className="text-right">Grand Total</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((i) => {
                            const isCancelled = i.status === "cancelled";
                            const ps = (i.payment_status || "").toLowerCase();
                            const paid = ps === "paid";
                            const partial = !paid && ps === "partially_paid";
                            const fullyMatched = !paid && ps === "fully_matched";
                            return (
                                <tr key={i.id} data-testid={`inv-row-${i.invoice_number}`}
                                    className={isCancelled ? "opacity-60" : ""}>
                                    <td className="hdb-mono font-semibold text-slate-900">
                                        {isCancelled && <span className="hdb-tag hdb-tag-red mr-2" style={{background:"#FEE2E2",color:"#991B1B"}}>CANCELLED</span>}
                                        <span className={isCancelled ? "line-through" : ""}>{i.invoice_number}</span>
                                    </td>
                                    <td className="hdb-mono">{i.invoice_date}</td>
                                    <td>{i.buyer.name}</td>
                                    <td>
                                        <span className={`hdb-tag ${i.is_interstate ? "hdb-tag-gold" : "hdb-tag-mint"}`}>
                                            {i.invoice_type} · {i.is_interstate ? "IGST" : "CGST+SGST"}
                                        </span>
                                    </td>
                                    <td className="hdb-num hdb-num-right">{fmt(i.subtotal)}</td>
                                    <td className="hdb-num hdb-num-right">{fmt(i.total_tax)}</td>
                                    <td className="hdb-num hdb-num-right font-bold">{fmt(i.grand_total)}</td>
                                    <td className="whitespace-nowrap">
                                        {!isCancelled && (paid ? (
                                            <span className="hdb-tag hdb-tag-mint" data-testid={`inv-status-${i.invoice_number}`}>Paid</span>
                                        ) : partial ? (
                                            <span className="hdb-tag hdb-tag-gold" data-testid={`inv-status-${i.invoice_number}`}>Partial</span>
                                        ) : fullyMatched ? (
                                            <span className="hdb-tag hdb-tag-mint" data-testid={`inv-status-${i.invoice_number}`}>Matched</span>
                                        ) : (
                                            <span className="hdb-tag" style={{background:"#FEE2E2",color:"#991B1B"}}
                                                  data-testid={`inv-status-${i.invoice_number}`}>Unpaid</span>
                                        ))}
                                    </td>
                                    <td className="text-right whitespace-nowrap">
                                        {!isCancelled && (paid ? (
                                            <button onClick={() => togglePaid(i, false)}
                                                    disabled={busyId === i.id}
                                                    className="hdb-btn hdb-btn-ghost mr-1"
                                                    data-testid={`mark-unpaid-${i.invoice_number}`}>
                                                {busyId === i.id
                                                    ? <Loader2 size={11} className="inline animate-spin"/>
                                                    : <><RotateCcw size={11}/> Unpaid</>}
                                            </button>
                                        ) : (
                                            <button onClick={() => togglePaid(i, true)}
                                                    disabled={busyId === i.id}
                                                    className="hdb-btn hdb-btn-mint mr-1"
                                                    data-testid={`mark-paid-${i.invoice_number}`}>
                                                {busyId === i.id
                                                    ? <Loader2 size={11} className="inline animate-spin"/>
                                                    : <><Check size={11}/> Mark Paid</>}
                                            </button>
                                        ))}
                                        <Link to={`/invoices/${i.id}`} className="hdb-btn hdb-btn-ghost"
                                              data-testid={`view-inv-${i.id}`}>
                                            <FileText size={13} /> View
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-400">
                                {selectedMonth === "all" ? "No invoices yet" : `No invoices in ${monthLabel(selectedMonth)}`}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div>
            <div className="hdb-label">{label}</div>
            <div className={`hdb-num font-black mt-1 ${highlight ? "text-emerald-700 text-2xl" : "text-slate-900 text-xl"}`}>{value}</div>
        </div>
    );
}
